import axios from 'axios';

const apiUrl = process.env.REACT_APP_BASIX_API || '';

// Pull the friendly filename out of a Content-Disposition header. Every PDF endpoint sets a
// standardized name (backend PdfHelper.FriendlyName → e.g. "2026-07-07 Chad Allen TIME-100055.pdf")
// and CORS exposes the header (Startup.cs WithExposedHeaders), so we can read it cross-origin. This
// is what restores our branded PDF naming — a bare blob object-URL shows only its random UUID in
// the browser's PDF viewer (Chad, 2026-07-07).
const filenameFromDisposition = (cd?: string): string | undefined => {
  if (!cd) return undefined;
  // RFC 5987 filename*=UTF-8''… wins if present, else the plain filename="…"
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(cd);
  if (star?.[1]) {
    try { return decodeURIComponent(star[1].trim().replace(/^"|"$/g, '')); } catch { /* fall through */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain?.[1]?.trim();
};

// item 546 — DOWNLOAD the PDF with its friendly filename (Chad: exports were named after the
// blob object-URL's UUID). A blob object-URL is `blob:<origin>/<uuid>`, and Chromium's inline
// viewer titles/saves the doc by THAT url — wrapping the blob in a named File doesn't change it,
// so inline preview can't carry a real name. An anchor with `download="<name>"` is the reliable
// path: the browser saves exactly that filename. We fetch as a blob (not a raw link) so the axios
// bearer interceptor still authenticates the request.
const downloadBlob = (data: Blob, filename?: string): void => {
  const url = URL.createObjectURL(new Blob([data], { type: data.type || 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename && filename.trim() ? filename : 'document.pdf';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // release the object URL after the download has kicked off
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

const escapeHtml = (s: string): string => s
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

// item 547 / 555 — PREVIEW: open the PDF inline in a new tab to view it (no save). A bare
// blob object-URL is `blob:<origin>/<uuid>`, and the browser titles the tab by THAT url — so
// preview used to read as a random UUID. We instead open a tiny same-origin wrapper page whose
// <title> is the friendly name and embed the PDF in a full-bleed iframe, so the tab reads e.g.
// "2026-07-09 AVPro Edge PO-100055". Falls back to a bare blob tab if the popup is wrapper-blocked.
const previewBlobInTab = (data: Blob, filename?: string): void => {
  const url = URL.createObjectURL(new Blob([data], { type: data.type || 'application/pdf' }));
  const name = filename && filename.trim() ? filename : 'document.pdf';
  const w = window.open('', '_blank');
  if (!w) {
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return;
  }
  w.document.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(name)}</title>`
    + '<style>html,body{margin:0;height:100%}iframe{border:0;width:100%;height:100%}</style></head>'
    + `<body><iframe src="${url}" title="${escapeHtml(name)}"></iframe></body></html>`,
  );
  w.document.close();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

// item 807 (Chad) — fetch a PDF endpoint (authed) and return an object URL for INLINE embed
// (an <iframe> can't carry the bearer header on its own). Caller must URL.revokeObjectURL.
export const pdfObjectUrl = async (path: string): Promise<string> => {
  const res = await axios.get(`${apiUrl}${path}`, { responseType: 'blob' });
  return URL.createObjectURL(new Blob([res.data], { type: res.data.type || 'application/pdf' }));
};

export const previewPdf = async (path: string): Promise<void> => {
  const res = await axios.get(`${apiUrl}${path}`, { responseType: 'blob' });
  previewBlobInTab(res.data, filenameFromDisposition(res.headers['content-disposition']));
};

// item 1424 — Stripe's own PDF for a subscription invoice (in_…), streamed through the API
export const previewStripeInvoicePdf = (stripeInvoiceId: string): Promise<void> => previewPdf(`payment/stripe-invoice-pdf/${stripeInvoiceId}`);

// filename overrides the server's Content-Disposition name when a caller knows a better one.
export const openPdf = async (path: string, filename?: string): Promise<void> => {
  const res = await axios.get(`${apiUrl}${path}`, { responseType: 'blob' });
  downloadBlob(res.data, filename ?? filenameFromDisposition(res.headers['content-disposition']));
};

// POST a body to a PDF endpoint and open the returned blob inline (item 114 — the KPI report
// renders numbers computed on the client).
export const postPdf = async (path: string, body: unknown, filename?: string): Promise<void> => {
  const res = await axios.post(`${apiUrl}${path}`, body, { responseType: 'blob' });
  downloadBlob(res.data, filename ?? filenameFromDisposition(res.headers['content-disposition']));
};

export default openPdf;
